import { useState, useRef, useCallback, useEffect } from "react";
import { Input } from "../../../components/ui/input";
import { Select } from "../../../components/ui/select";
import { useTranslation } from "../../../i18n";

const DEBOUNCE_MS = 500;

export default function CustomerSearch({ onResult }) {
  const { t } = useTranslation();
  const [searchBy, setSearchBy] = useState("0");
  const [query, setQuery] = useState("");
  const debounceRef = useRef(null);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const searchMapper = {
    0: {
      validate: (input) => /^[\d+\-()\s]*$/g.test(input),
      format: (input) => input.replace(/[+\-()\s]+/g, ""),
    },
    1: {
      validate: (input) => /^([a-zA-Zа-яА-Я]+\s?)*$/.test(input),
      format: (input) => input.trim(),
    },
    2: {
      validate: (input) => /^[^\s]*$/.test(input),
      format: (input) => input.trim(),
    },
  };

  const doSearch = useCallback(
    (value) => {
      const { validate, format } = searchMapper[searchBy];
      if (validate && !validate(value)) {
        return;
      }

      if (value === "") {
        onResult(null);
        return;
      }

      const formattedInput = format(value) ?? value;
      onResult({ categoryIdx: Number(searchBy), text: formattedInput });
    },
    [searchBy, onResult],
  );

  const debouncedSearch = useCallback(
    (value) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        doSearch(value);
      }, DEBOUNCE_MS);
    },
    [doSearch],
  );

  const handleChange = (e) => {
    const value = e.target.value;
    const { validate } = searchMapper[searchBy];
    if (validate && !validate(value)) {
      return;
    }
    setQuery(value);
    debouncedSearch(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      doSearch(query);
    }
  };

  return (
    <div className="flex gap-2 items-center">
      <Input
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={t("common.search")}
        className="lg:w-96"
      />
      <Select
        value={searchBy}
        onChange={(e) => {
          if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
          }
          setQuery("");
          setSearchBy(e.target.value);
          onResult(null);
        }}
        className="w-auto"
      >
        <option value="0">{t("common.byPhone")}</option>
        <option value="1">{t("common.byName")}</option>
        <option value="2">{t("common.byEmail")}</option>
      </Select>
    </div>
  );
}
